import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import DramaCard from '@/components/feature/DramaCard';
import { useFavorites } from '@/hooks/useFavorites';
import { useSeo } from '@/hooks/useSeo';
import { formatDate, getDramaById, getEpisodesByDrama, getRelatedDramas } from '@/lib/content';
import ShareDialog from './components/ShareDialog';
import EpisodeDirectory from './components/EpisodeDirectory';

export default function Detail() {
  const { id = '' } = useParams();
  const drama = getDramaById(id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [shareOpen, setShareOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useSeo({
    title: drama
      ? `迷人的保姆《${drama.title}》详情及剧情介绍 - 更新至第${drama.updatedEpisodes}集`
      : '剧集详情 - 迷人的保姆在线观看',
    description: drama
      ? `《${drama.title}》剧集详情页，包含导演、主演、类型与更新时间等信息，更新至第${drama.updatedEpisodes}集，并附完整剧情介绍与章节列表。`
      : '查看迷人的保姆及相关剧集的详细信息、剧情介绍与章节列表。',
    keywords: drama ? `${drama.title},${drama.title}剧情介绍,迷人的保姆在线观看,剧集更新` : '迷人的保姆剧情介绍',
  });

  useEffect(() => {
    setLoading(true);
    setError('');
    const timer = window.setTimeout(() => {
      try {
        setLoading(false);
      } catch {
        setError('剧集信息加载失败，请稍后重试。');
        setLoading(false);
      }
    }, 300);
    return () => window.clearTimeout(timer);
  }, [id]);

  if (loading) {
    return (
      <div className="w-full px-4 md:px-8 lg:px-12 py-20">
        <div className="h-64 w-full rounded-lg bg-background-100 animate-soft-pulse"></div>
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-8">
          <div className="aspect-[3/4] w-full rounded-lg bg-background-100 animate-soft-pulse"></div>
          <div className="space-y-4">
            <div className="h-6 w-1/3 rounded bg-background-100 animate-soft-pulse"></div>
            <div className="h-4 w-full rounded bg-background-100 animate-soft-pulse"></div>
            <div className="h-4 w-5/6 rounded bg-background-100 animate-soft-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full px-4 md:px-8 lg:px-12 py-24 text-center">
        <span className="mx-auto w-16 h-16 rounded-full bg-accent-100 flex items-center justify-center text-accent-700 mb-4">
          <i className="ri-error-warning-line text-3xl"></i>
        </span>
        <h1 className="text-xl font-semibold text-foreground-900">加载出错</h1>
        <p className="mt-2 text-sm text-foreground-600">{error}</p>
        <Link
          to="/category"
          className="mt-6 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-medium whitespace-nowrap"
        >
          返回剧集分类
        </Link>
      </div>
    );
  }

  if (!drama) {
    return (
      <div className="w-full px-4 md:px-8 lg:px-12 py-24 text-center">
        <span className="mx-auto w-16 h-16 rounded-full bg-background-100 flex items-center justify-center text-foreground-400 mb-4">
          <i className="ri-file-unknow-line text-3xl"></i>
        </span>
        <h1 className="text-xl font-semibold text-foreground-900">内容不存在</h1>
        <p className="mt-2 text-sm text-foreground-600">
          你访问的剧集可能已被移除或链接有误，去剧集分类看看其他内容吧。
        </p>
        <Link
          to="/category"
          className="mt-6 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-medium whitespace-nowrap"
        >
          返回剧集分类
        </Link>
      </div>
    );
  }

  const episodes = getEpisodesByDrama(drama.id);
  const aired = episodes.filter((e) => e.aired);
  const firstEpisode = aired[0];
  const latestEpisode = aired[aired.length - 1];
  const related = getRelatedDramas(drama.id, 4);
  const favorited = isFavorite(drama.id);

  const infoRows: { label: string; value: string }[] = [
    { label: '导演', value: drama.director },
    { label: '主演', value: drama.cast.join('、') },
    { label: '类型', value: drama.genres.join(' / ') },
    { label: '上映年份', value: String(drama.year) },
    { label: '地区', value: drama.region },
    { label: '语言', value: drama.language },
    { label: '更新时间', value: formatDate(drama.updatedAt) },
    { label: '集数', value: `更新至 ${drama.updatedEpisodes} / 共 ${drama.totalEpisodes} 集` },
  ];

  return (
    <div className="w-full">
      {/* 顶部视觉条 */}
      <section className="relative w-full h-[280px] md:h-[360px] overflow-hidden bg-primary-950">
        <img
          src={drama.backdrop}
          alt={`${drama.title}剧集背景`}
          title={`${drama.title} - 迷人的保姆在线观看`}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/70 to-primary-950/40"></div>
        <div className="relative z-10 h-full w-full px-4 md:px-8 lg:px-12 flex flex-col justify-between py-6 md:py-8">
          <div className="[&_a]:text-background-300 [&_span]:text-background-200 [&_.text-foreground-900]:!text-background-50 [&_.text-foreground-400]:!text-primary-400">
            <Breadcrumb
              items={[{ label: '首页', to: '/' }, { label: '剧集分类', to: '/category' }, { label: drama.title }]}
            />
          </div>
          <div className="max-w-3xl">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span
                className={`px-2.5 py-1 rounded-full text-[11px] font-medium ${
                  drama.status === 'ongoing'
                    ? 'bg-accent-500 text-background-50 dark:text-foreground-950'
                    : 'bg-secondary-500 text-background-50 dark:text-foreground-950'
                }`}
              >
                {drama.status === 'ongoing' ? '连载中' : '已完结'}
              </span>
              {drama.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-2.5 py-1 rounded-full text-[11px] bg-background-50/15 border border-background-50/30 text-background-100 backdrop-blur-sm"
                >
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="text-2xl md:text-4xl font-bold text-background-50">{drama.title}</h1>
            <p className="mt-1.5 font-label text-sm text-background-300">{drama.alias}</p>
          </div>
        </div>
      </section>

      {/* 主信息区 */}
      <section className="w-full bg-background-50 py-10 md:py-14">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-8 lg:gap-12 items-start">
            <div className="w-full max-w-[260px] mx-auto lg:mx-0 lg:max-w-none">
              <div className="rounded-lg overflow-hidden border border-background-200 bg-background-200">
                <div className="aspect-[3/4] w-full">
                  <img
                    src={drama.cover}
                    alt={`${drama.title}封面`}
                    title={`${drama.title} - 高清电视剧在线观看`}
                    className="w-full h-full object-top object-cover"
                  />
                </div>
              </div>
              <div className="mt-4 flex gap-3">
                <button
                  type="button"
                  onClick={() => toggleFavorite(drama.id)}
                  className={`flex-1 h-11 rounded-md text-sm font-medium inline-flex items-center justify-center gap-1.5 transition-colors whitespace-nowrap ${
                    favorited
                      ? 'bg-accent-500 text-background-50 dark:text-foreground-950'
                      : 'bg-background-50 border border-background-300 text-foreground-700 hover:border-accent-300 hover:text-accent-600'
                  }`}
                >
                  <i className={favorited ? 'ri-heart-3-fill' : 'ri-heart-3-line'}></i>
                  {favorited ? '已收藏' : '收藏'}
                </button>
                <button
                  type="button"
                  onClick={() => setShareOpen(true)}
                  className="flex-1 h-11 rounded-md text-sm font-medium inline-flex items-center justify-center gap-1.5 bg-background-50 border border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-colors whitespace-nowrap"
                >
                  <i className="ri-share-line"></i>
                  分享
                </button>
              </div>
              <p className="mt-2 text-[11px] text-foreground-400 text-center">
                收藏保存在本地浏览器，无需登录。
              </p>
            </div>

            <div className="min-w-0">
              <div className="flex flex-wrap gap-3">
                {firstEpisode && (
                  <Link
                    to={`/play/${firstEpisode.id}`}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-play-fill text-lg"></i>
                    从第 1 集开始
                  </Link>
                )}
                {latestEpisode && (
                  <Link
                    to={`/play/${latestEpisode.id}`}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-accent-500 text-background-50 dark:text-foreground-950 text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-fire-fill text-lg"></i>
                    观看最新第 {latestEpisode.number} 集
                  </Link>
                )}
              </div>

              <h2 className="mt-8 text-lg font-bold text-foreground-950 flex items-center gap-2">
                <span className="w-1 h-4 rounded-full bg-accent-500"></span>
                剧集信息
              </h2>
              <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-3">
                {infoRows.map((row) => (
                  <div
                    key={row.label}
                    className="flex gap-3 text-sm py-1.5 border-b border-background-200/70"
                  >
                    <dt className="text-foreground-500 w-20 shrink-0">{row.label}</dt>
                    <dd className="text-foreground-900 min-w-0">{row.value}</dd>
                  </div>
                ))}
              </dl>

              <h2 className="mt-9 text-lg font-bold text-foreground-950 flex items-center gap-2">
                <span className="w-1 h-4 rounded-full bg-accent-500"></span>
                剧情简介
              </h2>
              <div className="mt-4 space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                <p className="text-foreground-900 font-medium">{drama.synopsis}</p>
                <p>{drama.longSynopsis}</p>
                <p>
                  全剧以家庭日常为底色，通过一个个生活化的片段推进剧情，既有细腻的情感刻画，也有温暖治愈的表达。
                  更新安排为{drama.weekUpdate}，最新剧情动态可在首页与分类页持续关注。
                </p>
              </div>

              <div className="mt-6 flex flex-wrap gap-2">
                {drama.genres.map((genre) => (
                  <Link
                    key={genre}
                    to="/category"
                    className="px-3 py-1.5 rounded-full text-xs bg-secondary-100 text-secondary-900 hover:bg-secondary-200 transition-colors"
                  >
                    #{genre}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 集数目录 */}
      <section className="w-full bg-background-100 py-10 md:py-14">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <h2 className="text-lg md:text-xl font-bold text-foreground-950 mb-6 flex items-center gap-2">
            <i className="ri-movie-2-line text-primary-700"></i>
            集数目录
          </h2>
          <EpisodeDirectory episodes={episodes} />
        </div>
      </section>

      {/* 相关推荐 */}
      <section className="w-full bg-background-50 py-10 md:py-14">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="flex items-center justify-between gap-4 mb-6">
            <h2 className="text-lg md:text-xl font-bold text-foreground-950 flex items-center gap-2">
              <i className="ri-magic-line text-accent-600"></i>
              相关推荐
            </h2>
            <Link
              to="/category"
              className="text-sm text-primary-700 hover:text-primary-600 inline-flex items-center gap-1 whitespace-nowrap"
            >
              更多剧集
              <i className="ri-arrow-right-s-line text-lg"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {related.map((item) => (
              <DramaCard key={item.id} drama={item} />
            ))}
          </div>
        </div>
      </section>

      <ShareDialog open={shareOpen} onClose={() => setShareOpen(false)} title={`《${drama.title}》- 迷人的保姆在线观看`} />
    </div>
  );
}